﻿import { currencyFormatter, numberformatter } from "./formatters";
import "../css/range-slider.css";

class RangeSlider {
	el: HTMLElement;
	min: number;
	max: number;
	start: number;
	end: number | null;
	step: number;
	minLabelSelector: string;
	maxLabelSelector: string;
	sliderMinName: string;
	sliderMaxName: string;
	color: string | null;
	labelFormat: string | null;
	/**
	 * "UI only" mode: the slider drives existing named inputs instead of owning them. It then skips
	 * the querystring read/write, and leaves an empty label input empty on init so "no value" still
	 * means "no filter". Off by default, so every existing slider behaves exactly as before.
	 */
	uiOnly: boolean;

	sliderMin: HTMLInputElement;
	sliderMax: HTMLInputElement | null;
	sliderDiv: HTMLElement;
	inverseLeft: HTMLElement;
	inverseRight: HTMLElement;
	rangeDiv: HTMLElement;
	thumbLeft: HTMLElement;
	thumbRight: HTMLElement | null;

	private parseLabelValue(raw: string): number | null {
		const numeric = Number(raw.replace(/[^0-9.-]/g, ""));
		return Number.isFinite(numeric) ? numeric : null;
	}

	private clampValue(value: number, lower: number, upper: number) {
		return Math.min(Math.max(value, lower), upper);
	}

	constructor(triggerElement: HTMLElement) {
		this.el = triggerElement;
		this.min = Number(this.el.getAttribute("min") ?? 0);
		this.max = Number(this.el.getAttribute("max") ?? 100);
		this.start = Number(this.el.getAttribute("start") ?? 1);
		this.step = Number(this.el.getAttribute("step") ?? 1);
		this.minLabelSelector = this.el.getAttribute("min-label-selector") ?? "";
		this.maxLabelSelector = this.el.getAttribute("max-label-selector") ?? "";
		this.sliderMinName = this.el.getAttribute("min-input-name") ?? "SliderMinName";
		this.sliderMaxName = this.el.getAttribute("max-input-name") ?? "SliderMaxName";
		this.color = this.el.getAttribute("color") ?? null;
		this.labelFormat = this.el.getAttribute("label-format") ?? "currency";
		this.uiOnly = this.el.getAttribute("ui-only") !== null;
		const endv = this.el.getAttribute("end");
		this.end = endv !== null ? Number(endv) : null;

		// --- UI skeleton ---
		this.sliderDiv = document.createElement("div");
		this.inverseLeft = document.createElement("div");
		this.inverseRight = document.createElement("div");
		this.rangeDiv = document.createElement("div");
		this.thumbLeft = document.createElement("span");
		this.inverseLeft.setAttribute("inverse-left", "");
		this.inverseRight.setAttribute("inverse-right", "");
		this.rangeDiv.setAttribute("range", "");
		this.thumbLeft.setAttribute("thumb-left", "");
		this.sliderDiv.appendChild(this.inverseLeft);
		this.sliderDiv.appendChild(this.inverseRight);
		this.sliderDiv.appendChild(this.rangeDiv);
		this.sliderDiv.appendChild(this.thumbLeft);

		if (this.color) {
			this.thumbLeft.style.backgroundColor = this.color;
			this.rangeDiv.style.backgroundColor = this.color;
		}

		this.thumbRight = null;
		if (this.end !== null) {
			this.thumbRight = document.createElement("span");
			this.thumbRight.setAttribute("thumb-right", "");
			this.sliderDiv.appendChild(this.thumbRight);
			if (this.color) this.thumbRight.style.backgroundColor = this.color;
		}

		this.el.appendChild(this.sliderDiv);

		// --- Inputs ---
		this.sliderMin = document.createElement("input");
		this.sliderMin.type = "range";
		this.sliderMin.min = String(this.min);
		this.sliderMin.max = String(this.max);
		this.sliderMin.step = String(this.step);
		this.sliderMin.value = String(this.start);
		this.sliderMin.name = this.sliderMinName;
		this.el.appendChild(this.sliderMin);

		this.sliderMax = null;
		if (this.end !== null) {
			this.sliderMax = document.createElement("input");
			this.sliderMax.type = "range";
			this.sliderMax.min = String(this.min);
			this.sliderMax.max = String(this.max);
			this.sliderMax.step = String(this.step);
			this.sliderMax.value = String(this.end);
			this.sliderMax.name = this.sliderMaxName;
			this.el.appendChild(this.sliderMax);
		}

		// --- Hydrate from URL (keep existing values) ---
		this.hydrateFromQuery();

		// --- Allow manual label entry ---
		this.setupLabelListener(this.minLabelSelector, (val) => {
			const upperLimit = this.sliderMax ? Number(this.sliderMax.value) - 1 : this.max;
			const clamped = this.clampValue(val, this.min, upperLimit);
			this.sliderMin.value = String(clamped);
			this.sliderMinChanged(false);
		});

		if (this.sliderMax) {
			this.setupLabelListener(this.maxLabelSelector, (val) => {
				const lowerLimit = Number(this.sliderMin.value) + 1;
				const clamped = this.clampValue(val, lowerLimit, this.max);
				this.sliderMax!.value = String(clamped);
				this.sliderMaxChanged(false);
			});
		}

		// --- Initial paint ---
		this.sliderMinChanged(true);
		if (this.sliderMax) this.sliderMaxChanged(true);

		// --- Events (update URL only on user interaction) ---
		this.sliderMin.oninput = () => this.sliderMinChanged(false);
		if (this.sliderMax) this.sliderMax.oninput = () => this.sliderMaxChanged(false);
	}

	// ---------- URL helpers ----------
	private setQueryParam(name: string, value: string) {
		if (this.uiOnly) return;
		const url = new URL(window.location.href);
		if (url.searchParams.get(name) === value) return;
		url.searchParams.set(name, value);
		history.replaceState(null, "", url.toString());
	}

	private getQueryNumber(name: string): number | null {
		const url = new URL(window.location.href);
		const raw = url.searchParams.get(name);
		if (raw === null) return null;
		const n = Number(raw);
		return Number.isFinite(n) ? n : null;
	}

	// ---------- Init from existing URL values ----------
	private hydrateFromQuery() {
		if (this.uiOnly) return;
		const qMin = this.getQueryNumber(this.sliderMinName);
		const qMax = this.sliderMax ? this.getQueryNumber(this.sliderMaxName) : null;

		// clamp helpers
		const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

		// Set min first
		if (qMin !== null) {
			this.sliderMin.value = String(clamp(qMin, this.min, this.max));
		}

		// Then max (if present)
		if (this.sliderMax) {
			if (qMax !== null) {
				this.sliderMax.value = String(clamp(qMax, this.min, this.max));
			}

			// Ensure ordering: min <= max - 1
			const minNum = Number(this.sliderMin.value);
			const maxNum = Number(this.sliderMax.value);
			if (minNum >= maxNum) {
				// Nudge based on what exists
				if (qMin !== null && qMax === null) {
					this.sliderMax.value = String(clamp(minNum + 1, this.min, this.max));
				} else {
					this.sliderMin.value = String(clamp(maxNum - 1, this.min, this.max));
				}
			}
		}
	}

	// ---------- math ----------
	private convertToNewRange(originalValue: number, newMin: number, newMax: number) {
		const originalMin = 0;
		const originalMax = 100;
		const scale = (newMax - newMin) / (originalMax - originalMin);
		return (originalValue - originalMin) * scale + newMin;
	}

	// ---------- rendering + side effects ----------
	sliderMinChanged(isInit: boolean) {
		if (this.sliderMin && this.sliderMax) this.sliderMin.value = String(Math.min(Number(this.sliderMin.value), Number(this.sliderMax.value) - 1));

		const value = (100 / (this.max - this.min)) * Number(this.sliderMin.value) - (100 / (this.max - this.min)) * this.min;
		this.inverseLeft.style.width = value + "%";

		const newVal = this.convertToNewRange(value, 0, 95);
		this.thumbLeft.style.left = newVal + "%";

		if (this.end !== null) {
			this.rangeDiv.style.left = value + "%";
		} else {
			this.rangeDiv.style.left = "0";
			this.rangeDiv.style.right = 100 - value + "%";
		}

		if (!this.sliderMax) this.inverseRight.style.width = 100 - value + "%";

		const txt = document.querySelector(this.minLabelSelector) as HTMLInputElement;
		if (txt) {
			// Only update label if it's not focused (user isn't typing) or if it's initialization
			const keepEmpty = isInit && this.uiOnly && txt.value.trim() === "";
			if ((isInit || document.activeElement !== txt) && keepEmpty === false) {
				txt.value = this.formatValue(Number(this.sliderMin.value));
			}
			if (isInit === false) {
				txt.dispatchEvent(new Event("input", { bubbles: true, cancelable: true }));
			}
		}

		if (!isInit) {
			this.setQueryParam(this.sliderMinName, String(this.sliderMin.value));
		}
	}

	sliderMaxChanged(isInit: boolean) {
		if (this.sliderMin && this.sliderMax)
			this.sliderMax.value = String(Math.max(Number(this.sliderMax.value), Number(this.sliderMin.value) - -1));

		const value = (100 / (this.max - this.min)) * Number(this.sliderMax?.value) - (100 / (this.max - this.min)) * this.min;
		this.inverseRight.style.width = 100 - value + "%";
		this.rangeDiv.style.right = 100 - value + "%";

		const newVal = this.convertToNewRange(value, 4, 100);
		if (this.thumbRight) this.thumbRight.style.left = newVal + "%";

		const txt = document.querySelector(this.maxLabelSelector) as HTMLInputElement;
		if (txt) {
			// Only update label if it's not focused (user isn't typing) or if it's initialization
			const keepEmpty = isInit && this.uiOnly && txt.value.trim() === "";
			if ((isInit || document.activeElement !== txt) && keepEmpty === false) {
				txt.value = this.formatValue(Number(this.sliderMax?.value));
			}
			if (isInit === false) {
				txt.dispatchEvent(new Event("input", { bubbles: true, cancelable: true }));
			}
		}

		if (!isInit && this.sliderMax) {
			this.setQueryParam(this.sliderMaxName, String(this.sliderMax.value));
		}
	}

	private setupLabelListener(selector: string, applyValue: (value: number) => void) {
		if (!selector) return;
		const label = document.querySelector(selector);
		if (!(label instanceof HTMLInputElement)) return;

		label.removeAttribute("readonly");

		const syncValue = () => {
			const parsed = this.parseLabelValue(label.value);
			if (parsed === null) return;
			applyValue(parsed);
		};

		// Auto-select text when value is $0 and user focuses the field
		label.addEventListener("focus", () => {
			const parsed = this.parseLabelValue(label.value);
			if (parsed === 0) {
				label.select();
			}
		});

		// Update slider on every keystroke, but strip leading zeros first
		label.addEventListener("input", () => {
			const rawValue = label.value;
			// Extract just the numeric digits (no formatting)
			const digitsOnly = rawValue.replace(/[^0-9]/g, "");

			// Check if there are leading zeros followed by more digits (e.g., "05", "05000")
			// but not just "0" by itself or "0." for decimals
			if (digitsOnly.length > 1 && digitsOnly.startsWith("0") && !rawValue.match(/0\./)) {
				// Find the first digit in the original string
				const firstDigitMatch = rawValue.match(/[0-9]/);
				if (firstDigitMatch && firstDigitMatch.index !== undefined) {
					const firstDigitIndex = firstDigitMatch.index;
					// Find the last consecutive digit
					let lastDigitIndex = firstDigitIndex;
					while (lastDigitIndex < rawValue.length && /[0-9]/.test(rawValue[lastDigitIndex])) {
						lastDigitIndex++;
					}

					// Extract the digit sequence and strip leading zeros
					const digitSequence = rawValue.slice(firstDigitIndex, lastDigitIndex);
					const strippedSequence = digitSequence.replace(/^0+/, "") || "0";

					// Only update if we actually stripped zeros
					if (strippedSequence !== digitSequence) {
						const before = rawValue.slice(0, firstDigitIndex);
						const after = rawValue.slice(lastDigitIndex);
						const cursorPos = label.selectionStart || 0;
						label.value = before + strippedSequence + after;

						// Adjust cursor position
						const removedChars = digitSequence.length - strippedSequence.length;
						const newCursorPos = Math.max(firstDigitIndex, cursorPos - removedChars);
						label.setSelectionRange(newCursorPos, newCursorPos);
					}
				}
			}
			syncValue();
		});
		// Format the label when user finishes editing (blur or Enter)
		label.addEventListener("change", () => {
			syncValue();
			// Format the label after syncing
			const parsed = this.parseLabelValue(label.value);
			if (parsed !== null) {
				label.value = this.formatValue(parsed);
			}
		});
		label.addEventListener("keyup", (ev) => {
			if (ev.key === "Enter") {
				syncValue();
				// Format the label after syncing
				const parsed = this.parseLabelValue(label.value);
				if (parsed !== null) {
					label.value = this.formatValue(parsed);
				}
			}
		});
	}

	private formatValue(val: number) {
		if (this.labelFormat === "number") return numberformatter.format(val);
		else if (this.labelFormat === "currency") return currencyFormatter.format(val);
		return String(val);
	}
}

document.querySelectorAll<HTMLElement>("[range-slider]").forEach((el) => new RangeSlider(el));
